import * as S from './styles';
import { ClientProps } from '@/types';
import React, { useCallback, useEffect, useState } from 'react';
import { OrderItemsProps } from '@/talons/orderStatusPage/useOrderStatusPage';
import { ModalOrderStatus, OrderStatusCard, OrderStatusHeader as Header } from '@/components';
import { listPayloadsForOfflineView, listPayloadsForSynchedView } from '@/shared/ordersStore';

interface CardProps {
  clientName: string;
  client: ClientProps;
  orders: OrderItemsProps[];
  clientRefNumber: string;
  clientCnpj: string;
  isLast?: boolean;
  date?: string;
}

const Card = ({ clientName, orders, clientRefNumber, date, clientCnpj, client }: CardProps) => {
  const [isOpen, setIsOpen] = useState('');

  const toggleModal = useCallback((status: string) => {
    setIsOpen(status);
  }, []);

  const [mappedSynchedOrders, setMappedSynchedOrders] = useState(() => new Map());
  const [mappedOfflineOrders, setMappedOfflineOrders] = useState(() => new Map());

  useEffect(() => {
    let cancelled = false;

    const loadMaps = async () => {
      try {
        const [synchedOrders, offlineOrders] = await Promise.all([listPayloadsForSynchedView(), listPayloadsForOfflineView()]);
        if (cancelled) return;

        const synchedMap = new Map();
        synchedOrders.forEach((syncOrder) => {
          synchedMap.set(syncOrder?.orders?.order_number, syncOrder);
        });

        const offlineMap = new Map();
        offlineOrders.forEach((offlineOrder) => {
          offlineMap.set(offlineOrder?.orders?.order_number, offlineOrder);
        });

        setMappedSynchedOrders(synchedMap);
        setMappedOfflineOrders(offlineMap);
      } catch {
        if (!cancelled) {
          setMappedSynchedOrders(new Map());
          setMappedOfflineOrders(new Map());
        }
      }
    };

    void loadMaps();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <S.CardContainer key={`${clientRefNumber}-${clientCnpj}`}>
      <Header client={client} clientName={clientName} clientRefNumber={clientRefNumber} />
      <React.Fragment>
        {orders?.map((order, index) => {
          if (order?.orderDate === date && orders.length > 0) {
            return (
              <div key={`order-content-card-${clientRefNumber}-${clientCnpj}-${index}`}>
                <OrderStatusCard
                  order={order}
                  mappedSynchedOrders={mappedSynchedOrders}
                  mappedOfflineOrders={mappedOfflineOrders}
                  toggleModal={toggleModal}
                  isLast={index === orders?.length - 1}
                  key={`order-status-card-${clientRefNumber}-${clientCnpj}-${index}`}
                />
                <ModalOrderStatus
                  order={order}
                  client={client}
                  clientCnpj={clientCnpj}
                  clientName={clientName}
                  clientRefNumber={clientRefNumber}
                  closeModal={() => toggleModal('')}
                  isOpen={order?.orderNumber === isOpen}
                  clientUnilever={client?.UNILEVER}
                  key={`order-status-modal-${clientRefNumber}-${clientCnpj}-${index}`}
                />
              </div>
            );
          }
          return null;
        })}
      </React.Fragment>
    </S.CardContainer>
  );
};

export default Card;
